<script setup lang="ts">
import HeaderLogo from "./header/HeaderLogo.vue";
import HeaderActions from "./header/HeaderActions.vue";
import MegaNavItem from "./header/MegaNavItem.vue";
import MegaMobileNavItem from "./header/MegaMobileNavItem.vue";
import MobileMenuFooter from "./header/MobileMenuFooter.vue";
import { useSearchProvider } from "~/composables/searchProvider";

const route = useRoute();
const locale = computed(() => localeFromUrl(route.path));

// The whole header bar is now <e-mega-menu>: it owns the horizontal nav, the
// hover/focus mega-dropdown panels, the overflow→hamburger collapse, the mobile
// drawer (relocating the same authored items — so the old combined
// MobileNavDrawer is gone), the `--header-height` CSS variable, and the global
// Cmd/Ctrl+K shortcut (dispatched to us as `mega:search`).
const sections = useNavSections(locale);
const { data: settings } = useSettings(locale);
// Panel footer "read more" link label — authored per locale under
// `header.read_more` in the content `settings.md`; falls back to "Read more"
// until a locale fills it. (Shares HeaderActions' useSettings cache.)
const viewAllLabel = computed(
  () => settings.value?.header?.read_more ?? "Read more",
);
const searchLabel = computed(() => settings.value?.header?.search ?? "Search");

// Search on mobile: <HeaderActions> is `hidden md:flex`, so below md the bar
// had no search at all. A one-tap button in the bar (next to the hamburger)
// rather than in the drawer, which would cost an extra tap to open first.

const searchProvider = useSearchProvider();
const searchOpen = ref(false);
const searchMounted = ref(false);
watch(searchOpen, (v) => {
  if (v) searchMounted.value = true;
});

// Warm the search palette + index on intent so the open is instant — same
// optimisation as before, just triggered by the element's `mega:search` and by
// the Cmd/Ctrl modifier keydown (the actual open is owned by <e-mega-menu>).
function warmSearch() {
  searchMounted.value = true;
  searchProvider.value?.load();
}
function openSearch() {
  warmSearch();
  searchOpen.value = true;
}
function onModifier(e: KeyboardEvent) {
  if (e.key === "Meta" || e.key === "Control") warmSearch();
}
onMounted(() => {
  window.addEventListener("keydown", onModifier);

  void customElements.whenDefined("e-mega-menu").then(async () => {
    // Wait for the post-mount flush that adds each panel (MegaNavItem gates
    // <e-mega-menu-panel> behind isMounted).
    await nextTick();
    const el = megaRef.value;
    if (!el) return;

    // The mega-menu's mobile drawer is created without `hide-header` (unlike its
    // dropdown drawer), so the e-drawer renders a default close-bar we don't
    // want. The element doesn't exportparts the drawer, so reach it once
    // upgraded and hide its header. (Lib gap — see report.) Drawer still closes
    // via link click / click-outside / the hamburger toggle.
    el.shadowRoot
      ?.querySelector('[part="drawer"]')
      ?.setAttribute("hide-header", "");

    // Defer the mobile-drawer nav tree (~80 <e-book-menu-item>s) until the
    // drawer is first opened. On desktop the hamburger never shows, so `open`
    // is never set and those elements never render or upgrade — removing them
    // from the initial SSR HTML and, crucially, from the synchronous custom-
    // element upgrade burst that dominates homepage TBT. On mobile the first
    // hamburger tap sets `open`; we render the tree one tick later (drawer
    // opens empty for a frame on that first tap only).
    if (el.hasAttribute("open")) showMobileNav.value = true;
    openObs = new MutationObserver(() => {
      if (el.hasAttribute("open")) {
        showMobileNav.value = true;
        openObs?.disconnect();
      }
    });
    openObs.observe(el, { attributes: true, attributeFilter: ["open"] });
    // Late-panel relocation is handled by the element itself as of
    // @econnect/ui-megamenu 0.1.0-alpha-20260630143153 (the item dispatches
    // `mega-item:panel` when its panel mounts post-hydration and the menu
    // re-runs relocatePanels), so the former throwaway-slottable probe is gone.
  });
});
onUnmounted(() => {
  window.removeEventListener("keydown", onModifier);
  openObs?.disconnect();
});

// <e-book-menu> leaf anchors do a full load by default; intercept book:navigate
// for SPA. The mega-menu doesn't know about the custom mobile-slot links, so
// also close its mobile drawer (it observes the `open` attribute on the host).
const megaRef = ref<HTMLElement>();
useNavHighlightDedupe(megaRef, sections);

// Gates the mobile-drawer nav tree: false until the drawer is first opened
// (see the MutationObserver in onMounted). Keeps ~80 <e-book-menu-item>s out of
// the desktop render + upgrade burst.
const showMobileNav = ref(false);
let openObs: MutationObserver | undefined;

// Close the mobile drawer on any navigation — covers the book-menu links, the
// footer language switch, and the account menu (the mega-menu observes `open`
// on its host). onBookNavigate also closes it directly for instant feedback.
watch(
  () => route.path,
  () => megaRef.value?.removeAttribute("open"),
);

function onBookNavigate(e: Event) {
  const detail = (e as CustomEvent<{ to?: string; originalEvent?: Event }>)
    .detail;
  if (!detail?.to) return;
  detail.originalEvent?.preventDefault?.();
  megaRef.value?.removeAttribute("open");
  navigateTo(detail.to);
}

// The mega-menu's bar items, panel links, and feature cards render plain
// <a href> anchors in shadow DOM and the element emits no navigate event — so a
// click does a full document load instead of SPA navigation. Intercept clicks
// that bubble (composed) to the host: for a same-origin, unmodified left-click
// on an internal anchor, cancel the native nav and hand it to navigateTo. The
// mobile <e-book-menu> has its own `book:navigate` path, so skip clicks inside
// it to avoid double-navigating.
function onMegaNavigate(e: MouseEvent) {
  if (
    e.defaultPrevented ||
    e.button !== 0 ||
    e.metaKey ||
    e.ctrlKey ||
    e.shiftKey ||
    e.altKey
  )
    return;
  const path = e.composedPath();
  if (path.some((n) => n instanceof Element && n.localName === "e-book-menu"))
    return;
  const anchor = path.find(
    (n): n is HTMLAnchorElement => n instanceof HTMLAnchorElement,
  );
  const href = anchor?.getAttribute("href");
  if (!href || anchor!.target === "_blank") return;
  const url = new URL(href, window.location.href);
  if (url.origin !== window.location.origin) return; // external → full load
  e.preventDefault();
  megaRef.value?.removeAttribute("open");
  navigateTo(url.pathname + url.search + url.hash);
}

const { data: homePages } = useHomeLocales();
const languages = computed(() =>
  [
    ...new Set(
      (homePages.value ?? [])
        .map((p) => (p.stem as string).split("/")[0])
        .filter((s): s is string => Boolean(s)),
    ),
  ].map((code) => ({
    value: code,
    label: new Intl.DisplayNames([code], { type: "language" }).of(code) ?? code,
  })),
);
</script>

<template>
  <e-mega-menu
    ref="megaRef"
    sticky
    class="z-[60]! part-[bar]:max-w-(--breakpoint-4xl)! part-[dropdown-content]:max-w-(--breakpoint-4xl)! part-[base]:bg-(--color-elevation-50)/90! part-[base]:border-(--color-elevation-200)!"
    @mega:search="openSearch"
    @click="onMegaNavigate"
  >
    <HeaderLogo slot="leading" :locale="locale" />

    <MegaNavItem
      v-for="section in sections"
      :key="section.slug"
      :section="section"
      :view-all-label="viewAllLabel"
    />

    <HeaderActions
      slot="trailing"
      :locale="locale"
      :languages="languages"
      @search="openSearch"
    />
    <e-button
      slot="trailing"
      class="md:hidden"
      variant="ghost"
      icon="search"
      square
      :aria-label="searchLabel"
      @click="openSearch"
    />

    <div v-if="showMobileNav" slot="mobile" class="flex min-h-full flex-col">
      <e-book-menu
        class="mobile-nav-tree min-h-0 flex-1 overflow-y-auto"
        @book:navigate="onBookNavigate"
      >
        <MegaMobileNavItem
          v-for="section in sections"
          :key="section.slug"
          :node="section"
        />
      </e-book-menu>
      <ClientOnly>
        <MobileMenuFooter :locale="locale" :languages="languages" />
      </ClientOnly>
    </div>
  </e-mega-menu>

  <ClientOnly>
    <LazySearchOverlay
      v-if="searchOpen || searchMounted"
      :open="searchOpen"
      @close="searchOpen = false"
    />
  </ClientOnly>
</template>

<style scoped>
/* Subtle divider between each root section in the mobile drawer tree. Direct
   children of the menu only (`>`), so nested rows keep their tight grouping. */
.mobile-nav-tree > :deep(e-book-menu-item + e-book-menu-item)::part(summary),
.mobile-nav-tree > :deep(e-book-menu-item + e-book-menu-item)::part(link) {
  border-top: 1px solid var(--color-elevation-200);
  border-radius: 0;
}

/* Fatter touch targets: bump vertical padding on every tappable row so a thumb
   press can't slip between items. */
.mobile-nav-tree :deep(e-book-menu-item)::part(summary),
.mobile-nav-tree :deep(e-book-menu-item)::part(link) {
  padding-top: 0.875rem;
  padding-bottom: 0.875rem;
}

/* Tab-group header: a small-caps category title, not a tappable row. Tighter
   than a link, with breathing room above to separate one group from the next. */
.mobile-nav-tree :deep(.nav-label)::part(link) {
  padding-top: 1rem;
  padding-bottom: 0.25rem;
  cursor: default;
}
.mobile-nav-tree :deep(.nav-label)::part(label) {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-elevation-500);
}

/* Items belonging to a tab-group header: indented so they read as nested under
   the label above, not siblings of it. */
.mobile-nav-tree :deep(.nav-grouped)::part(link) {
  padding-left: 2.25rem;
}
</style>
